import { createContext, useContext, useState, useEffect, useCallback } from "react";

var STORAGE_KEY = "rgp-cart";
var CartContext = createContext();

export function CartProvider({ children }) {
  var [cartItems, setCartItems] = useState([]);
  var [isCartOpen, setIsCartOpen] = useState(false);

  // Load cart from localStorage on mount
  useEffect(function loadCart() {
    try {
      var saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setCartItems(JSON.parse(saved));
      }
    } catch (e) {
      console.error("Failed to load cart from localStorage:", e);
    }
  }, []);

  // Save cart to localStorage when it changes
  useEffect(function saveCart() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cartItems));
    } catch (e) {
      console.error("Failed to save cart to localStorage:", e);
    }
  }, [cartItems]);

  var addToCart = useCallback(function(product, qty, addons) {
    // Create a unique cart item ID that includes addon configuration
    var addonKey = addons && addons.length > 0 
      ? JSON.stringify(addons.map(function(a) { return a.id + ":" + a.quantity; }).sort()) 
      : "";
    var cartItemId = product.id + (addonKey ? "-" + addonKey : "");
    
    setCartItems(function(prev) {
      var existing = prev.find(function(item) { return item.cartItemId === cartItemId; });
      if (existing) {
        return prev.map(function(item) {
          if (item.cartItemId === cartItemId) {
            return { ...item, quantity: item.quantity + qty };
          }
          return item;
        });
      }
      return [...prev, { ...product, cartItemId: cartItemId, quantity: qty, selectedAddons: addons || [] }];
    });
  }, []);

  var removeFromCart = useCallback(function(cartItemId) {
    setCartItems(function(prev) { 
      return prev.filter(function(item) { return item.cartItemId !== cartItemId; }); 
    });
  }, []);

  var updateQuantity = useCallback(function(cartItemId, qty) {
    if (qty < 1) {
      setCartItems(function(prev) { 
        return prev.filter(function(item) { return item.cartItemId !== cartItemId; }); 
      });
      return;
    }
    setCartItems(function(prev) {
      return prev.map(function(item) {
        if (item.cartItemId === cartItemId) return { ...item, quantity: qty };
        return item;
      });
    });
  }, []);

  var clearCart = useCallback(function() { 
    setCartItems([]); 
  }, []);

  var getCartTotal = useCallback(function() {
    return cartItems.reduce(function(total, item) {
      var basePrice = item.sale_price || item.price;
      var addonTotal = 0;
      if (item.selectedAddons && item.selectedAddons.length > 0) {
        item.selectedAddons.forEach(function(addon) {
          addonTotal += addon.price * addon.quantity;
        });
      }
      return total + (basePrice + addonTotal) * item.quantity;
    }, 0);
  }, [cartItems]);

  var getCartCount = useCallback(function() {
    return cartItems.reduce(function(count, item) { return count + item.quantity; }, 0);
  }, [cartItems]);

  var value = { 
    cartItems: cartItems, 
    isCartOpen: isCartOpen, 
    setIsCartOpen: setIsCartOpen, 
    addToCart: addToCart, 
    removeFromCart: removeFromCart, 
    updateQuantity: updateQuantity, 
    clearCart: clearCart, 
    getCartTotal: getCartTotal, 
    getCartCount: getCartCount 
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  var context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within a CartProvider");
  return context;
}
